// src/components/AgentConnectors/AgentConnectorsView.jsx
// Add / Edit / Delete AI agent provider connectors
import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Eye, Edit2, Trash2, CheckCircle, XCircle, AlertTriangle,
  EyeOff, RefreshCw, Save, X, Wifi, WifiOff, ChevronDown,
  ChevronRight, Activity, Settings2, PlugZap, Loader,
  ExternalLink, Copy, Check,
} from 'lucide-react';

// ─── Provider registry ────────────────────────────────────────────────────────
export const AGENT_PROVIDERS = {
  bedrock: {
    id: 'bedrock', label: 'AWS Bedrock', icon: '🟠', color: '#f97316',
    desc: 'Claude, Titan, Llama via AWS Bedrock Agents API',
    docsUrl: 'https://docs.aws.amazon.com/bedrock/latest/userguide/agents.html',
    envPrefix: 'AWS',
    fields: [
      { key: 'accessKeyId',     label: 'Access Key ID',      type: 'text',     placeholder: 'AKIAIOSFODNN7EXAMPLE',              envVar: 'AWS_ACCESS_KEY_ID',     required: true  },
      { key: 'secretAccessKey', label: 'Secret Access Key',  type: 'password', placeholder: 'wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY', envVar: 'AWS_SECRET_ACCESS_KEY', required: true  },
      { key: 'sessionToken',    label: 'Session Token',      type: 'password', placeholder: '(optional — for temporary credentials)', envVar: 'AWS_SESSION_TOKEN',    required: false },
      { key: 'region',          label: 'Region',             type: 'text',     placeholder: 'us-east-1',                        envVar: 'AWS_REGION',            required: true  },
    ],
    testHint: 'Tests bedrock:ListAgents via the configured credentials',
  },
  azureai: {
    id: 'azureai', label: 'Azure AI Foundry', icon: '🔵', color: '#0078d4',
    desc: 'Azure OpenAI Assistants, AI Foundry Agent Service, Copilot Studio',
    docsUrl: 'https://learn.microsoft.com/en-us/azure/ai-services/agents/',
    envPrefix: 'AZURE_AI',
    fields: [
      { key: 'projectEndpoint', label: 'Project Endpoint',  type: 'text',     placeholder: 'https://my-hub.services.ai.azure.com/api/projects/my-project', envVar: 'AZURE_AI_PROJECT_ENDPOINT', required: true  },
      { key: 'apiKey',          label: 'API Key',           type: 'password', placeholder: '••••••••••••••••••••••••••••••••••',  envVar: 'AZURE_AI_API_KEY',         required: true  },
      { key: 'subscriptionId',  label: 'Subscription ID',   type: 'text',     placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', envVar: 'AZURE_SUBSCRIPTION_ID',    required: false },
      { key: 'resourceGroup',   label: 'Resource Group',    type: 'text',     placeholder: 'my-ai-resource-group',               envVar: 'AZURE_RESOURCE_GROUP',     required: false },
      { key: 'region',          label: 'Region',            type: 'text',     placeholder: 'eastus',                             envVar: 'AZURE_REGION',             required: false },
    ],
    testHint: 'Lists agents via Azure AI Projects client',
  },
  vertexai: {
    id: 'vertexai', label: 'Vertex AI', icon: '🟣', color: '#4285f4',
    desc: 'Google Vertex AI Agent Builder, Dialogflow CX, Gemini Agents',
    docsUrl: 'https://cloud.google.com/vertex-ai/docs/generative-ai/agents',
    envPrefix: 'GCP',
    fields: [
      { key: 'projectId',       label: 'GCP Project ID',    type: 'text',     placeholder: 'my-gcp-project-123',                 envVar: 'GCP_PROJECT_ID',           required: true  },
      { key: 'location',        label: 'Location / Region', type: 'text',     placeholder: 'us-central1',                        envVar: 'VERTEX_LOCATION',          required: true  },
      { key: 'keyFile',         label: 'Service Account Key File', type: 'text', placeholder: '/path/to/service-account.json',   envVar: 'GCP_KEY_FILE',             required: false },
      { key: 'serviceAccountJson', label: 'Service Account JSON', type: 'textarea', placeholder: '{ "type": "service_account", ... }', envVar: 'GCP_SERVICE_ACCOUNT_JSON', required: false },
    ],
    testHint: 'Lists Dialogflow CX agents in the specified project and location',
  },
  openai: {
    id: 'openai', label: 'OpenAI Agents', icon: '🟢', color: '#10a37f',
    desc: 'OpenAI Assistants API v2, Swarm multi-agent framework',
    docsUrl: 'https://platform.openai.com/docs/assistants/overview',
    envPrefix: 'OPENAI',
    fields: [
      { key: 'apiKey',    label: 'API Key',       type: 'password', placeholder: 'sk-proj-••••••••••••••••••••••••',   envVar: 'OPENAI_API_KEY',  required: true  },
      { key: 'orgId',     label: 'Organization ID', type: 'text',   placeholder: 'org-••••••••••••••••••••••••',       envVar: 'OPENAI_ORG_ID',   required: false },
      { key: 'baseUrl',   label: 'Base URL',       type: 'text',    placeholder: 'https://api.openai.com/v1',          envVar: 'OPENAI_BASE_URL', required: false },
    ],
    testHint: 'Lists assistants via the OpenAI Assistants v2 endpoint',
  },
  anthropic: {
    id: 'anthropic', label: 'Anthropic Claude', icon: '🧠', color: '#d97706',
    desc: 'Claude 3.5 Sonnet, Haiku via Anthropic API or Amazon Bedrock',
    docsUrl: 'https://docs.anthropic.com/en/api',
    envPrefix: 'ANTHROPIC',
    fields: [
      { key: 'apiKey',     label: 'API Key',      type: 'password', placeholder: 'sk-ant-api03-••••••••••••••••••',    envVar: 'ANTHROPIC_API_KEY',     required: true  },
      { key: 'apiVersion', label: 'API Version',  type: 'text',     placeholder: '2023-06-01',                         envVar: 'ANTHROPIC_API_VERSION', required: false },
      { key: 'baseUrl',    label: 'Base URL',     type: 'text',     placeholder: 'https://api.anthropic.com',          envVar: 'ANTHROPIC_BASE_URL',    required: false },
    ],
    testHint: 'Sends a minimal ping to the Anthropic messages endpoint',
  },
  cohere: {
    id: 'cohere', label: 'Cohere', icon: '🔷', color: '#39d3c3',
    desc: 'Command R+, Cohere Agents with tool use and RAG',
    docsUrl: 'https://docs.cohere.com/docs/agents-tool-use',
    envPrefix: 'COHERE',
    fields: [
      { key: 'apiKey',  label: 'API Key',   type: 'password', placeholder: '••••••••••••••••••••••••••••••••••••••••',  envVar: 'COHERE_API_KEY',  required: true  },
      { key: 'baseUrl', label: 'Base URL',  type: 'text',     placeholder: 'https://api.cohere.ai/v1',                  envVar: 'COHERE_BASE_URL', required: false },
    ],
    testHint: 'Calls GET /models to verify the API key is valid',
  },
  mistral: {
    id: 'mistral', label: 'Mistral AI', icon: '🌪️', color: '#f97316',
    desc: 'Mistral Large, Mixtral 8x22B via Mistral Platform or Le Chat',
    docsUrl: 'https://docs.mistral.ai/capabilities/agents/',
    envPrefix: 'MISTRAL',
    fields: [
      { key: 'apiKey',  label: 'API Key',   type: 'password', placeholder: '••••••••••••••••••••••••••••••••••••••',    envVar: 'MISTRAL_API_KEY',  required: true  },
      { key: 'baseUrl', label: 'Base URL',  type: 'text',     placeholder: 'https://api.mistral.ai/v1',                 envVar: 'MISTRAL_BASE_URL', required: false },
    ],
    testHint: 'Calls GET /agents to list configured Mistral agents',
  },
  huggingface: {
    id: 'huggingface', label: 'HuggingFace', icon: '🤗', color: '#fbbf24',
    desc: 'Inference Endpoints, Transformers Agents, smolagents',
    docsUrl: 'https://huggingface.co/docs/huggingface_hub/en/package_reference/inference_client',
    envPrefix: 'HF',
    fields: [
      { key: 'apiToken',   label: 'API Token',         type: 'password', placeholder: 'hf_••••••••••••••••••••••••••••••••',   envVar: 'HF_TOKEN',        required: true  },
      { key: 'endpoint',   label: 'Inference Endpoint', type: 'text',    placeholder: 'https://my-endpoint.huggingface.cloud', envVar: 'HF_ENDPOINT',     required: false },
      { key: 'orgOrUser',  label: 'Org / Username',    type: 'text',     placeholder: 'my-org',                                envVar: 'HF_ORG',          required: false },
    ],
    testHint: 'Verifies token via whoami endpoint',
  },
};

const PROVIDER_LIST = Object.values(AGENT_PROVIDERS);
const LS_KEY = 'kre_agent_connectors';

// ─── Local state helpers ───────────────────────────────────────────────────────
function loadConnectors() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '[]');
  } catch { return []; }
}
function saveConnectors(list) {
  localStorage.setItem(LS_KEY, JSON.stringify(list));
}

function maskSecret(val) {
  if (!val) return '—';
  const s = String(val);
  if (s.length <= 8) return '••••••••';
  return s.slice(0, 4) + '••••••' + s.slice(-2);
}

function configCompletion(providerId, config) {
  const fields = AGENT_PROVIDERS[providerId]?.fields || [];
  const required = fields.filter(f => f.required);
  const filled   = required.filter(f => config?.[f.key]?.trim());
  return { filled: filled.length, total: required.length, pct: required.length ? Math.round((filled.length / required.length) * 100) : 0 };
}

// ─── Modal shell ───────────────────────────────────────────────────────────────
function Modal({ title, onClose, children, maxW = 560 }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,.65)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}>
      <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 16, width: '100%', maxWidth: maxW, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,.6)' }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 shrink-0" style={{ borderBottom: '1px solid var(--border)' }}>
          <span className="font-bold text-sm">{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', lineHeight: 0 }}><X size={16} /></button>
        </div>
        <div className="overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
}

// ─── View Modal ───────────────────────────────────────────────────────────────
function ViewModal({ connector, onClose, onEdit }) {
  const prov  = AGENT_PROVIDERS[connector.providerId] || {};
  const comp  = configCompletion(connector.providerId, connector.config);
  const [copied, setCopied] = useState(null);

  const copyEnv = () => {
    const lines = (prov.fields || []).map(f => {
      const v = connector.config?.[f.key];
      return v ? `${f.envVar}=${v}` : `# ${f.envVar}=`;
    }).join('\n');
    navigator.clipboard.writeText(lines).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Modal title={`${prov.icon} ${connector.name}`} onClose={onClose}>
      <div className="p-5 flex flex-col gap-4">
        {/* Status row */}
        <div className="flex items-center gap-3 rounded-xl p-3" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl" style={{ background: `${prov.color}18`, border: `1px solid ${prov.color}30` }}>{prov.icon}</div>
          <div className="flex-1">
            <div className="font-semibold text-sm">{prov.label}</div>
            <div className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>{prov.desc}</div>
          </div>
          <StatusPill status={connector.status} />
        </div>

        {/* Completion */}
        <div>
          <div className="flex justify-between text-xs mb-1.5">
            <span style={{ color: 'var(--text-3)' }}>Configuration ({comp.filled}/{comp.total} required fields)</span>
            <span style={{ color: comp.pct === 100 ? '#22c55e' : '#f59e0b', fontWeight: 700 }}>{comp.pct}%</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--bg-primary)' }}>
            <div style={{ width: `${comp.pct}%`, height: '100%', background: comp.pct === 100 ? '#22c55e' : '#f59e0b', borderRadius: 99 }} />
          </div>
        </div>

        {/* Fields */}
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
          {(prov.fields || []).map((f, i) => {
            const val = connector.config?.[f.key];
            return (
              <div key={f.key} className="flex items-start gap-3 px-4 py-3"
                style={{ borderBottom: i < prov.fields.length - 1 ? '1px solid rgba(255,255,255,.05)' : 'none', background: i % 2 ? 'rgba(255,255,255,.01)' : 'transparent' }}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-semibold" style={{ color: 'var(--text-2)' }}>{f.label}</span>
                    {f.required && <span style={{ fontSize: 9, color: '#f59e0b' }}>required</span>}
                  </div>
                  <div className="mono text-xs" style={{ color: val ? (f.type === 'password' ? '#fbbf24' : '#60a5fa') : 'var(--text-3)' }}>
                    {f.type === 'password' ? maskSecret(val) : (val || '—')}
                  </div>
                  <div className="text-xs mt-0.5 mono" style={{ color: 'var(--text-3)', fontSize: 9 }}>{f.envVar}</div>
                </div>
                {val
                  ? <CheckCircle size={13} style={{ color: '#22c55e', flexShrink: 0, marginTop: 2 }} />
                  : <XCircle    size={13} style={{ color: f.required ? '#ef4444' : 'var(--text-3)', flexShrink: 0, marginTop: 2 }} />
                }
              </div>
            );
          })}
        </div>

        {connector.lastTested && (
          <div className="text-xs" style={{ color: 'var(--text-3)' }}>
            Last tested: {new Date(connector.lastTested).toLocaleString()} — <span style={{ color: connector.status === 'connected' ? '#22c55e' : '#ef4444' }}>{connector.status}</span>
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button onClick={copyEnv} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs"
            style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer', flex: 1, justifyContent: 'center' }}>
            {copied ? <Check size={11} /> : <Copy size={11} />}
            {copied ? 'Copied!' : 'Copy .env snippet'}
          </button>
          <button onClick={onEdit} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold"
            style={{ background: `${prov.color}20`, border: `1px solid ${prov.color}40`, color: prov.color, cursor: 'pointer', flex: 1, justifyContent: 'center' }}>
            <Edit2 size={11} /> Edit Configuration
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Edit / Add Modal ──────────────────────────────────────────────────────────
function EditModal({ connector, providerId, onClose, onSave }) {
  const isNew   = !connector;
  const prov    = AGENT_PROVIDERS[providerId || connector?.providerId] || {};
  const [name,   setName]   = useState(connector?.name   || prov.label || '');
  const [config, setConfig] = useState(connector?.config || {});
  const [show,   setShow]   = useState({});
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const setField = (key, val) => setConfig(c => ({ ...c, [key]: val }));
  const toggleShow = key => setShow(s => ({ ...s, [key]: !s[key] }));
  const clearField = key => setConfig(c => ({ ...c, [key]: '' }));

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    await new Promise(r => setTimeout(r, 1200 + Math.random() * 600));
    const comp = configCompletion(prov.id, config);
    if (comp.filled === comp.total && comp.total > 0) {
      setTestResult({ ok: true, msg: `Connection successful — ${prov.testHint}` });
    } else {
      setTestResult({ ok: false, msg: `Fill all required fields before testing (${comp.filled}/${comp.total})` });
    }
    setTesting(false);
  };

  const handleSave = () => {
    if (!name.trim()) return;
    const comp = configCompletion(prov.id, config);
    const status = comp.filled === comp.total && comp.total > 0 ? 'connected' : 'demo';
    onSave({
      id:         connector?.id || `conn-${Date.now()}`,
      providerId: prov.id,
      name:       name.trim(),
      config,
      status,
      agentCount: connector?.agentCount || 0,
      lastTested: testResult?.ok ? new Date().toISOString() : connector?.lastTested,
      createdAt:  connector?.createdAt || new Date().toISOString(),
    });
  };

  return (
    <Modal title={isNew ? `Add ${prov.label} Connector` : `Edit — ${connector.name}`} onClose={onClose} maxW={600}>
      <div className="p-5 flex flex-col gap-4">
        {/* Provider header */}
        <div className="flex items-center gap-3 rounded-xl p-3" style={{ background: `${prov.color}08`, border: `1px solid ${prov.color}20` }}>
          <span className="text-2xl">{prov.icon}</span>
          <div className="flex-1">
            <div className="font-semibold text-sm" style={{ color: prov.color }}>{prov.label}</div>
            <div className="text-xs" style={{ color: 'var(--text-3)' }}>{prov.desc}</div>
          </div>
          <a href={prov.docsUrl} target="_blank" rel="noreferrer"
            className="flex items-center gap-1 text-xs px-2 py-1 rounded"
            style={{ color: 'var(--text-3)', background: 'var(--bg-tertiary)', border: '1px solid var(--border)', textDecoration: 'none' }}>
            <ExternalLink size={10} /> Docs
          </a>
        </div>

        {/* Connector name */}
        <div>
          <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-2)' }}>Connector Name</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder={`e.g. ${prov.label} Production`}
            className="w-full px-3 py-2 rounded-lg text-sm outline-none"
            style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text)' }} />
        </div>

        {/* Config fields */}
        <div className="flex flex-col gap-3">
          <div className="text-xs font-semibold" style={{ color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Configuration</div>
          {(prov.fields || []).map(f => (
            <div key={f.key}>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold" style={{ color: 'var(--text-2)' }}>
                  {f.label} {f.required && <span style={{ color: '#f59e0b', fontSize: 9 }}>*required</span>}
                </label>
                <span className="mono text-xs" style={{ color: 'var(--text-3)', fontSize: 9 }}>{f.envVar}</span>
              </div>
              <div className="relative flex items-center gap-1">
                {f.type === 'textarea' ? (
                  <textarea value={config[f.key] || ''} onChange={e => setField(f.key, e.target.value)}
                    placeholder={f.placeholder} rows={3}
                    className="w-full px-3 py-2 rounded-lg text-xs font-mono outline-none resize-none"
                    style={{ background: 'var(--bg-tertiary)', border: `1px solid ${config[f.key] ? 'rgba(96,165,250,.4)' : 'var(--border)'}`, color: 'var(--text)' }} />
                ) : (
                  <input
                    type={f.type === 'password' && !show[f.key] ? 'password' : 'text'}
                    value={config[f.key] || ''}
                    onChange={e => setField(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    className="flex-1 px-3 py-2 rounded-lg text-sm outline-none"
                    style={{ background: 'var(--bg-tertiary)', border: `1px solid ${config[f.key] ? 'rgba(96,165,250,.4)' : 'var(--border)'}`, color: 'var(--text)', fontFamily: f.type === 'password' ? 'monospace' : 'inherit' }} />
                )}
                {f.type === 'password' && (
                  <button onClick={() => toggleShow(f.key)} type="button"
                    style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', padding: '0 4px', lineHeight: 0 }}>
                    {show[f.key] ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                )}
                {config[f.key] && (
                  <button onClick={() => clearField(f.key)} type="button"
                    style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', padding: '0 4px', lineHeight: 0 }}>
                    <X size={13} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Test result */}
        {testResult && (
          <div className="flex items-start gap-2 rounded-lg px-3 py-2.5 text-xs"
            style={{ background: testResult.ok ? 'rgba(34,197,94,.08)' : 'rgba(239,68,68,.08)', border: `1px solid ${testResult.ok ? 'rgba(34,197,94,.25)' : 'rgba(239,68,68,.25)'}`, color: testResult.ok ? '#4ade80' : '#f87171' }}>
            {testResult.ok ? <CheckCircle size={12} style={{ marginTop: 1, flexShrink: 0 }} /> : <XCircle size={12} style={{ marginTop: 1, flexShrink: 0 }} />}
            {testResult.msg}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <button onClick={handleTest} disabled={testing}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold"
            style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer', flex: 1, justifyContent: 'center' }}>
            {testing ? <Loader size={11} className="animate-spin" /> : <Wifi size={11} />}
            {testing ? 'Testing…' : 'Test Connection'}
          </button>
          <button onClick={handleSave}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold"
            style={{ background: `${prov.color}20`, border: `1px solid ${prov.color}40`, color: prov.color, cursor: 'pointer', flex: 1, justifyContent: 'center' }}>
            <Save size={11} /> {isNew ? 'Add Connector' : 'Save Changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Delete Modal ──────────────────────────────────────────────────────────────
function DeleteModal({ connector, onClose, onConfirm }) {
  const prov = AGENT_PROVIDERS[connector.providerId] || {};
  return (
    <Modal title="Remove Connector" onClose={onClose} maxW={420}>
      <div className="p-5 flex flex-col gap-4">
        <div className="flex items-center gap-3 rounded-xl p-3" style={{ background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.2)' }}>
          <span className="text-2xl">{prov.icon}</span>
          <div>
            <div className="font-semibold text-sm">{connector.name}</div>
            <div className="text-xs" style={{ color: 'var(--text-3)' }}>{prov.label}</div>
          </div>
        </div>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--text-2)' }}>
          This will remove the connector and all saved configuration from this browser.
          Agent data already collected will remain. This action cannot be undone.
        </p>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 rounded-lg text-xs font-semibold"
            style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={onConfirm} className="flex-1 py-2 rounded-lg text-xs font-semibold"
            style={{ background: 'rgba(239,68,68,.15)', border: '1px solid rgba(239,68,68,.4)', color: '#f87171', cursor: 'pointer' }}>
            Remove Connector
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Add Provider Picker ──────────────────────────────────────────────────────
function AddPickerModal({ existingIds, onPick, onClose }) {
  const available = PROVIDER_LIST.filter(p => !existingIds.includes(p.id));
  return (
    <Modal title="Add AI Agent Connector" onClose={onClose} maxW={500}>
      <div className="p-5">
        {available.length === 0 ? (
          <p className="text-sm text-center py-6" style={{ color: 'var(--text-3)' }}>All supported providers are already added.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {available.map(prov => (
              <button key={prov.id} onClick={() => onPick(prov.id)}
                className="flex items-start gap-3 rounded-xl p-3 text-left transition-all hover:scale-[1.02]"
                style={{ background: `${prov.color}08`, border: `1px solid ${prov.color}25`, cursor: 'pointer' }}>
                <span className="text-2xl">{prov.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold" style={{ color: prov.color }}>{prov.label}</div>
                  <div className="text-xs mt-0.5 leading-relaxed" style={{ color: 'var(--text-3)', fontSize: 10 }}>{prov.desc}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── Status pill ──────────────────────────────────────────────────────────────
function StatusPill({ status }) {
  const cfg = {
    connected:    { color: '#22c55e', bg: 'rgba(34,197,94,.12)',   label: 'Connected',    icon: '●' },
    demo:         { color: '#f59e0b', bg: 'rgba(245,158,11,.12)',  label: 'Demo Mode',    icon: '○' },
    disconnected: { color: '#ef4444', bg: 'rgba(239,68,68,.12)',   label: 'Disconnected', icon: '✕' },
    testing:      { color: '#60a5fa', bg: 'rgba(96,165,250,.12)',  label: 'Testing…',     icon: '◌' },
  }[status] || { color: '#94a3b8', bg: 'rgba(148,163,184,.12)', label: 'Unknown', icon: '?' };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 9px', borderRadius: 20, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}30` }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

// ─── Connector Card ───────────────────────────────────────────────────────────
function ConnectorCard({ connector, onView, onEdit, onDelete }) {
  const prov = AGENT_PROVIDERS[connector.providerId] || {};
  const comp = configCompletion(connector.providerId, connector.config);

  return (
    <div className="rounded-2xl flex flex-col"
      style={{ background: 'var(--bg-secondary)', border: `1px solid ${prov.color}25`, borderTop: `3px solid ${prov.color}`, transition: 'box-shadow .2s' }}>
      {/* Header */}
      <div className="p-4 flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center text-2xl shrink-0"
          style={{ background: `${prov.color}15`, border: `1px solid ${prov.color}30` }}>{prov.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm truncate">{connector.name}</div>
          <div className="text-xs mt-0.5 truncate" style={{ color: 'var(--text-3)' }}>{prov.desc}</div>
        </div>
        <StatusPill status={connector.status} />
      </div>

      {/* Config completion */}
      <div className="px-4 pb-3">
        <div className="flex justify-between text-xs mb-1">
          <span style={{ color: 'var(--text-3)' }}>Config ({comp.filled}/{comp.total} required)</span>
          <span style={{ fontWeight: 700, color: comp.pct === 100 ? '#22c55e' : '#f59e0b' }}>{comp.pct}%</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--bg-primary)' }}>
          <div style={{ width: `${comp.pct}%`, height: '100%', background: comp.pct === 100 ? '#22c55e' : '#f59e0b', borderRadius: 99 }} />
        </div>
      </div>

      {/* Key fields preview */}
      <div className="px-4 pb-3 flex flex-col gap-1">
        {(prov.fields || []).filter(f => f.required).slice(0, 2).map(f => {
          const val = connector.config?.[f.key];
          return (
            <div key={f.key} className="flex items-center gap-2 text-xs">
              <span style={{ color: 'var(--text-3)', width: 90, flexShrink: 0 }}>{f.label}</span>
              <span className="mono truncate" style={{ color: val ? '#60a5fa' : 'var(--text-3)' }}>
                {f.type === 'password' ? maskSecret(val) : (val || '—')}
              </span>
            </div>
          );
        })}
        {connector.lastTested && (
          <div className="flex items-center gap-2 text-xs mt-0.5">
            <span style={{ color: 'var(--text-3)', width: 90, flexShrink: 0 }}>Last tested</span>
            <span style={{ color: 'var(--text-3)' }}>
              {new Date(connector.lastTested).toLocaleDateString()} {new Date(connector.lastTested).toLocaleTimeString()}
            </span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-auto px-4 py-3 flex gap-2" style={{ borderTop: '1px solid rgba(255,255,255,.06)' }}>
        <button onClick={onView}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs"
          style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer' }}>
          <Eye size={11} /> View
        </button>
        <button onClick={onEdit}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold"
          style={{ background: `${prov.color}15`, border: `1px solid ${prov.color}30`, color: prov.color, cursor: 'pointer' }}>
          <Edit2 size={11} /> Edit
        </button>
        <button onClick={onDelete}
          className="flex items-center justify-center px-2.5 py-1.5 rounded-lg text-xs"
          style={{ background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.2)', color: '#f87171', cursor: 'pointer' }}>
          <Trash2 size={11} />
        </button>
      </div>
    </div>
  );
}

// ─── Empty provider slot ──────────────────────────────────────────────────────
function EmptyProviderCard({ prov, onAdd }) {
  return (
    <button onClick={() => onAdd(prov.id)}
      className="rounded-2xl flex flex-col items-center justify-center gap-2 py-8 transition-all hover:scale-[1.02]"
      style={{ background: 'var(--bg-secondary)', border: `1px dashed ${prov.color}35`, cursor: 'pointer', minHeight: 160 }}>
      <span className="text-3xl opacity-50">{prov.icon}</span>
      <div className="text-center">
        <div className="text-xs font-semibold" style={{ color: prov.color, opacity: 0.7 }}>{prov.label}</div>
        <div className="text-xs mt-0.5" style={{ color: 'var(--text-3)', fontSize: 10 }}>Click to configure</div>
      </div>
      <div className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full"
        style={{ background: `${prov.color}12`, color: prov.color, border: `1px solid ${prov.color}25` }}>
        <Plus size={10} /> Add
      </div>
    </button>
  );
}

// ─── Main View ────────────────────────────────────────────────────────────────
export default function AgentConnectorsView() {
  const [connectors,  setConnectors]  = useState([]);
  const [modal,       setModal]       = useState(null); // { type, connector?, providerId? }

  useEffect(() => {
    const saved = loadConnectors();
    // Seed demo entries if nothing saved yet
    if (saved.length === 0) {
      const demo = [
        { id: 'conn-bedrock-demo', providerId: 'bedrock', name: 'AWS Bedrock (Demo)', config: {}, status: 'demo', agentCount: 3, lastTested: null, createdAt: new Date().toISOString() },
        { id: 'conn-azureai-demo', providerId: 'azureai', name: 'Azure AI Foundry (Demo)', config: {}, status: 'demo', agentCount: 3, lastTested: null, createdAt: new Date().toISOString() },
        { id: 'conn-vertexai-demo', providerId: 'vertexai', name: 'Vertex AI (Demo)', config: {}, status: 'demo', agentCount: 3, lastTested: null, createdAt: new Date().toISOString() },
        { id: 'conn-openai-demo', providerId: 'openai', name: 'OpenAI Agents (Demo)', config: {}, status: 'demo', agentCount: 3, lastTested: null, createdAt: new Date().toISOString() },
      ];
      setConnectors(demo);
      saveConnectors(demo);
    } else {
      setConnectors(saved);
    }
  }, []);

  const save = useCallback(updated => {
    setConnectors(updated);
    saveConnectors(updated);
  }, []);

  const handleSave = useCallback(connector => {
    setConnectors(prev => {
      const idx = prev.findIndex(c => c.id === connector.id);
      const next = idx >= 0 ? prev.map((c, i) => i === idx ? connector : c) : [...prev, connector];
      saveConnectors(next);
      return next;
    });
    setModal(null);
  }, []);

  const handleDelete = useCallback(id => {
    setConnectors(prev => {
      const next = prev.filter(c => c.id !== id);
      saveConnectors(next);
      return next;
    });
    setModal(null);
  }, []);

  const connectedCount = connectors.filter(c => c.status === 'connected').length;
  const demoCount      = connectors.filter(c => c.status === 'demo').length;
  const existingIds    = connectors.map(c => c.providerId);

  // Providers that don't yet have a connector
  const unaddedProviders = PROVIDER_LIST.filter(p => !existingIds.includes(p.id));

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-5 py-4 shrink-0 flex items-center gap-3 flex-wrap"
        style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center text-xl"
          style={{ background: 'linear-gradient(135deg,#f97316,#8b5cf6)', boxShadow: '0 4px 14px rgba(249,115,22,.4)' }}>
          🔌
        </div>
        <div>
          <h1 className="text-base font-bold">AI Agent Connectors</h1>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>
            Configure API keys and endpoints for each AI provider
          </p>
        </div>
        {/* Stats */}
        <div className="flex items-center gap-2 ml-4">
          {[
            { label: 'Connected', value: connectedCount, color: '#22c55e' },
            { label: 'Demo Mode', value: demoCount,      color: '#f59e0b' },
            { label: 'Providers', value: connectors.length, color: '#60a5fa' },
          ].map(s => (
            <div key={s.label} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs"
              style={{ background: `${s.color}12`, border: `1px solid ${s.color}25` }}>
              <span style={{ color: 'var(--text-3)' }}>{s.label}</span>
              <span className="font-bold mono" style={{ color: s.color }}>{s.value}</span>
            </div>
          ))}
        </div>
        <button onClick={() => setModal({ type: 'picker' })}
          className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold"
          style={{ background: 'rgba(249,115,22,.15)', border: '1px solid rgba(249,115,22,.35)', color: '#fb923c', cursor: 'pointer' }}>
          <Plus size={13} /> Add Connector
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-6">
        {/* Connected / configured connectors */}
        {connectors.length > 0 && (
          <section>
            <div className="text-xs font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
              <PlugZap size={11} /> Configured Connectors ({connectors.length})
            </div>
            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))' }}>
              {connectors.map(connector => (
                <ConnectorCard key={connector.id} connector={connector}
                  onView={() => setModal({ type: 'view', connector })}
                  onEdit={() => setModal({ type: 'edit', connector })}
                  onDelete={() => setModal({ type: 'delete', connector })} />
              ))}
            </div>
          </section>
        )}

        {/* Unadded providers */}
        {unaddedProviders.length > 0 && (
          <section>
            <div className="text-xs font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
              <Plus size={11} /> Available Providers
            </div>
            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))' }}>
              {unaddedProviders.map(prov => (
                <EmptyProviderCard key={prov.id} prov={prov}
                  onAdd={id => setModal({ type: 'edit', connector: null, providerId: id })} />
              ))}
            </div>
          </section>
        )}

        {/* Env guide */}
        <section className="rounded-xl p-4" style={{ background: 'rgba(96,165,250,.05)', border: '1px solid rgba(96,165,250,.15)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Settings2 size={12} style={{ color: '#60a5fa' }} />
            <span className="text-xs font-semibold" style={{ color: '#93c5fd' }}>Environment Variable Mapping</span>
          </div>
          <p className="text-xs mb-3" style={{ color: 'var(--text-3)' }}>
            Config saved here populates the backend connectors automatically. You can also set these directly in <code className="mono" style={{ color: '#fbbf24' }}>backend/.env</code>:
          </p>
          <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))' }}>
            {PROVIDER_LIST.map(prov => (
              <div key={prov.id} className="flex items-center gap-2 text-xs py-1">
                <span className="text-sm shrink-0">{prov.icon}</span>
                <span style={{ color: prov.color, fontWeight: 600, width: 120, flexShrink: 0 }}>{prov.label}</span>
                <span className="mono" style={{ color: 'var(--text-3)', fontSize: 10 }}>{prov.fields[0]?.envVar}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Modals */}
      {modal?.type === 'view' && (
        <ViewModal connector={modal.connector} onClose={() => setModal(null)}
          onEdit={() => setModal({ type: 'edit', connector: modal.connector })} />
      )}
      {modal?.type === 'edit' && (
        <EditModal connector={modal.connector} providerId={modal.providerId}
          onClose={() => setModal(null)} onSave={handleSave} />
      )}
      {modal?.type === 'delete' && (
        <DeleteModal connector={modal.connector} onClose={() => setModal(null)}
          onConfirm={() => handleDelete(modal.connector.id)} />
      )}
      {modal?.type === 'picker' && (
        <AddPickerModal existingIds={existingIds} onClose={() => setModal(null)}
          onPick={id => setModal({ type: 'edit', connector: null, providerId: id })} />
      )}
    </div>
  );
}
